/**
 * BottomNavBar - Redesigned to match Open app's minimal aesthetic
 * 5 icons: Library | Insights | TODAY (center) | New Entry | Settings
 * Fixed position, always visible, works on all screen sizes
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TrendingUp, BookOpen, PenLine, Settings } from 'lucide-react';

export default function BottomNavBar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation items configuration
  const navItems = [
    {
      id: 'library',
      label: null,
      icon: BookOpen,
      href: '/library',
      ariaLabel: 'Library'
    },
    {
      id: 'insights',
      label: null,
      icon: TrendingUp,
      href: '/insights',
      ariaLabel: 'Insights'
    },
    {
      id: 'today',
      label: 'HOME',
      icon: 'logo', // Special case for center logo
      href: '/profile',
      ariaLabel: 'Home - Journal',
      isCenter: true
    },
    {
      id: 'new',
      label: null,
      icon: PenLine,
      href: '/journal/new',
      ariaLabel: 'New Entry'
    },
    {
      id: 'settings',
      label: null,
      icon: Settings,
      href: '/settings',
      ariaLabel: 'Settings'
    }
  ];

  // Check if current path is active
  const isActive = (href) => {
    if (!href) return false;
    if (href === '/profile') return location.pathname === '/' || location.pathname === '/profile';
    return location.pathname.startsWith(href);
  };

  // Handle navigation
  const handleClick = (item) => {
    if (item.href) {
      navigate(item.href);
    }
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 bg-[#0a0806]/95 backdrop-blur-xl border-t border-white/5"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 8px)',
        boxShadow: '0 -2px 16px rgba(0, 0, 0, 0.3)'
      }}
      aria-label="Main navigation"
    >
      {/* Navigation items container - compact height */}
      <div className="flex items-center justify-around px-4 h-[60px] max-w-2xl mx-auto">
        {navItems.map((item) => {
          const active = isActive(item.href);
          const IconComponent = item.icon;
          const isCenter = item.isCenter;

          return (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              className={`
                flex flex-col items-center justify-center gap-0.5 relative
                transition-all duration-300 ease-out
                min-w-[44px] min-h-[44px]
                rounded-xl
                group
                outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb85e] focus-visible:ring-offset-0
                ${active && !isCenter ? 'bg-white/10' : 'hover:bg-white/5'}
              `}
              aria-label={item.ariaLabel}
              aria-current={active ? 'page' : undefined}
              style={{ touchAction: 'manipulation' }}
            >
              {/* Icon or Logo */}
              {isCenter ? (
                // Center "TODAY" with Clarity logo - compact
                <div className="flex flex-col items-center gap-0.5">
                  {/* Logo Circle - compact */}
                  <div
                    className={`
                      w-8 h-8 rounded-full
                      flex items-center justify-center
                      bg-gradient-to-br from-white via-[#e8e3db] to-[#d4cfc5]
                      shadow-sm shadow-black/20
                      transition-all duration-300
                      ${active ? 'scale-105 shadow-md shadow-white/10' : 'scale-100 group-hover:scale-105'}
                    `}
                  >
                    <span className="text-base font-light text-[#1a1410]">
                      C
                    </span>
                  </div>

                  {/* TODAY Label - compact */}
                  <span
                    className={`
                      text-[11px] font-medium tracking-wider uppercase
                      transition-all duration-300
                      ${active ? 'text-white opacity-100' : 'text-white/40 opacity-50'}
                    `}
                  >
                    {item.label}
                  </span>
                </div>
              ) : (
                // Regular icons - compact
                <div
                  className={`
                    transition-all duration-300
                    ${active ? 'text-white opacity-100 scale-105' : 'text-white/40 opacity-50 group-hover:opacity-80 group-hover:scale-105'}
                  `}
                >
                  <IconComponent size={20} strokeWidth={active ? 2.5 : 2} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
